import React from 'react';
import { Box, Typography, ButtonBase, Card, CardContent, Skeleton } from '@mui/material';
import { cardSx } from '../theme';
import { useSkeletonAnimation } from './LoadingSkeletons';

// Amounts are the thing being compared, so they get tabular figures — otherwise
// the digits change width between tiles/rows and the column visibly jitters.
export const tabularNumsSx = { fontVariantNumeric: 'tabular-nums' };

// Tile geometry, named so the "two rows tall" floor below is computed from
// the same numbers the tile itself uses rather than a second, driftable copy.
const TILE_MIN_HEIGHT = 56; // SummaryTile's own minHeight
const TILE_BORDER = 1; // SummaryTile's border-width, same on top and bottom
const TILE_HEIGHT = TILE_MIN_HEIGHT + TILE_BORDER * 2;
const STRIP_ROW_GAP = 8; // matches summaryStripSx's gap: 1 (theme spacing unit)
const TWO_ROW_STRIP_HEIGHT = TILE_HEIGHT * 2 + STRIP_ROW_GAP;

// The panel's own vertical padding at each breakpoint — matches both the
// summary Paper's `p` and SummaryTotalCard's CardContent `py` below. MUI
// breakpoint objects carry a value forward until the next key overrides it,
// so "sm" is still the xs figure here; only "md" steps up.
const PANEL_PADDING_Y_SM = 1.25 * 8;
const PANEL_PADDING_Y_MD = 2 * 8;

// The wrapping grid on Expenses/Income only ever grows on sm+ (mobile keeps
// its compact single-line strip, on purpose — see the SummaryTotalCard
// comment below), so this is the floor for the panel and the "Total X" card
// beside it: however few tiles/rows exist right now, both stay at least as
// tall as a full two rows would be, so the header doesn't visibly resize as
// rows or a breakdown come and go. It's a minimum, not a cap — unlike the
// fixed-height approach this replaced, a third or fourth row still grows the
// panel taller instead of clipping.
export const summaryPanelMinHeight = {
  xs: 'none',
  sm: TWO_ROW_STRIP_HEIGHT + PANEL_PADDING_Y_SM * 2,
  md: TWO_ROW_STRIP_HEIGHT + PANEL_PADDING_Y_MD * 2,
};

// The category summary strip on Expenses and Income. Wraps to as many rows as
// it needs — an earlier version capped it at a fixed height and scrolled past
// that, which reliably clipped real content because how many rows a category
// list needs depends on window width, not on any fixed assumption.
export const summaryStripSx = {
  display: 'flex',
  gap: 1,
  flexWrap: { xs: 'nowrap', sm: 'wrap' },
  overflowX: { xs: 'auto', sm: 'visible' },
  scrollSnapType: { xs: 'x proximity', sm: 'none' },
  WebkitOverflowScrolling: 'touch',
  pb: { xs: 0.75, sm: 0 },
  '&::-webkit-scrollbar': { height: 6, width: 6 },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.18)', borderRadius: 3 },
};

/**
 * The "Total X" card sitting to the left of a category summary strip
 * (Expenses, Income). Kept as one component so the two pages can't drift
 * apart in tile size or number styling the way they previously did.
 */
export const SummaryTotalCard = ({ label, value, formatCurrency, loading = false }) => (
  <Card sx={{ height: '100%', minHeight: summaryPanelMinHeight }}>
    <CardContent
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        alignItems: { xs: 'baseline', md: 'flex-start' },
        justifyContent: 'space-between',
        gap: 1,
        py: { xs: 1.25, md: 2 },
        px: { xs: 1.5, md: 2 },
        '&:last-child': { pb: { xs: 1.25, md: 2 } },
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: '1.5rem', md: '2rem' },
          fontWeight: 700,
          lineHeight: 1.15,
          ...tabularNumsSx,
        }}
      >
        {/* While loading, a $0.00 here would read as a real total. */}
        {loading ? <TotalSkeleton /> : `$${formatCurrency(value)}`}
      </Typography>
    </CardContent>
  </Card>
);

const TotalSkeleton = () => <Skeleton animation={useSkeletonAnimation()} width="6ch" />;

/** Tile-shaped placeholders for the summary strip while its first load is in flight. */
export const SummaryStripSkeleton = ({ tiles = 5 }) => {
  const animation = useSkeletonAnimation();
  return (
    <Box sx={summaryStripSx} aria-hidden="true">
      {Array.from({ length: tiles }, (_, i) => (
        <Skeleton
          key={i}
          animation={animation}
          variant="rounded"
          sx={{ flex: '0 0 auto', width: 132, height: TILE_HEIGHT, borderRadius: 1.5 }}
        />
      ))}
    </Box>
  );
};

/**
 * One category/type tile in a summary strip.
 *
 * Rendered as a ButtonBase rather than a Box with onClick so the category
 * filter is reachable by keyboard and announces its pressed state; breakdown
 * tiles pass no onClick and stay inert.
 */
export const SummaryTile = ({ label, amount, share, selected, onClick, formatCurrency: format }) => {
  const isZero = !amount;
  const interactive = typeof onClick === 'function';
  // A ButtonBase on a non-button element still takes role="button" and a tab
  // stop, so the inert breakdown tiles render as a plain Box instead.
  const Root = interactive ? ButtonBase : Box;
  const interactiveProps = interactive
    ? {
      onClick,
      focusRipple: true,
      'aria-pressed': selected,
      'aria-label': `${label}, $${format(amount)}`,
    }
    : {};
  return (
    <Root
      {...interactiveProps}
      sx={{
        flex: '0 0 auto',
        scrollSnapAlign: 'start',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 0.4,
        minWidth: 132,
        minHeight: 56,
        px: 1,
        py: 1,
        borderRadius: 1.5,
        textAlign: 'left',
        cursor: interactive ? 'pointer' : 'default',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'transparent',
        boxShadow: cardSx.boxShadow,
        bgcolor: selected ? 'primary.main' : 'background.default',
        color: selected ? 'primary.contrastText' : (isZero ? 'text.disabled' : 'text.primary'),
        transition: 'background-color .15s ease, border-color .15s ease',
        ...(interactive && {
          '&:hover': {
            bgcolor: selected ? 'primary.dark' : 'action.hover',
          },
        }),
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Typography
        variant="caption"
        noWrap
        title={label}
        sx={{ fontWeight: 600, lineHeight: 1.2, display: 'block' }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.85rem', lineHeight: 1.2, ...tabularNumsSx }}>
        ${format(amount)}
      </Typography>
      {/* Proportion bar: the amounts alone make you compare six-digit strings.
          Sized against the largest tile, and purely supplementary to the text. */}
      <Box
        sx={{
          height: 3,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: selected ? 'rgba(255,255,255,0.28)' : 'divider',
        }}
      >
        <Box
          sx={{
            width: `${Math.max(0, Math.min(100, share))}%`,
            height: '100%',
            bgcolor: selected ? 'primary.contrastText' : 'primary.light',
          }}
        />
      </Box>
    </Root>
  );
};
